/**
 * Send a simple invoice email for mail/check orders (used from checkout and admin orders).
 */

import { MAIL_ORDER_MODE_TEST } from './mailOrderConstants';

const ORDERS_EMAIL_ADDRESS = 'orders@cranfordpub.ca';
const ORDERS_EMAIL_FROM = '"Orders Cranford Publications" <orders@cranfordpub.ca>';

function formatMoney(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

export function isMailOrder(order) {
  if (order?.mail_order_mode === 'live' || order?.mail_order_mode === 'test') return true;
  return /^FID-MAIL/i.test(String(order?.order_number || ''));
}

export function resolveMailOrderMode(order) {
  if (order?.mail_order_mode === 'test' || order?.mail_order_mode === 'live') {
    return order.mail_order_mode;
  }
  const orderNumber = String(order?.order_number || '');
  if (/FID-MAIL-TEST-/i.test(orderNumber)) return MAIL_ORDER_MODE_TEST;
  if (/^FID-MAIL-/i.test(orderNumber)) return 'live';
  return null;
}

export function isMailOrderTest(order) {
  return resolveMailOrderMode(order) === MAIL_ORDER_MODE_TEST;
}

function decorateTestInvoiceHtml(html) {
  const banner = `<div style="margin:0 0 20px;padding:14px 16px;background:#fef3c7;border:2px solid #f59e0b;border-radius:8px;color:#92400e;font-weight:600;text-align:center;">
    TEST MAIL ORDER — for training only. Not a live customer invoice.
  </div>`;
  return html.replace('<body', '<body').replace(
    /<body([^>]*)>/,
    `<body$1>${banner}`
  );
}

function buildSimpleInvoiceHtml(order, checkInstructions = '') {
  const items = order.items || [];
  const itemRows = items
    .map(
      (item) => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;">${item.name || `Product #${item.id}`}</td>
        <td style="padding:8px;text-align:center;border-bottom:1px solid #e5e7eb;">${item.quantity}</td>
        <td style="padding:8px;text-align:right;border-bottom:1px solid #e5e7eb;">${formatMoney(item.unitPrice || item.price_cents || item.price)}</td>
        <td style="padding:8px;text-align:right;border-bottom:1px solid #e5e7eb;">${formatMoney((item.unitPrice || item.price_cents || item.price) * item.quantity)}</td>
      </tr>`
    )
    .join('');

  const paymentBlock = checkInstructions
    ? `<div style="margin-top:20px;padding:16px;background:#fffbeb;border-left:4px solid #f59e0b;border-radius:6px;">
        <h3 style="margin:0 0 8px;color:#92400e;font-size:16px;">Payment instructions</h3>
        <p style="margin:0;color:#78350f;line-height:1.6;font-size:14px;">${checkInstructions.replace(/\n/g, '<br>')}</p>
      </div>`
    : '';

  let html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Invoice ${order.order_number}</title></head>
<body style="font-family:Arial,sans-serif;color:#333;margin:24px;">
  <h2 style="color:#22c55e;margin:0 0 8px;">Cranford Publications</h2>
  <p style="margin:0 0 16px;color:#666;">Invoice for order <strong>${order.order_number}</strong></p>
  <p style="margin:0 0 16px;"><strong>${order.customer_name || ''}</strong><br>${order.customer_email || ''}</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;">
    <thead><tr style="background:#f3f4f6;">
      <th style="padding:8px;text-align:left;">Item</th>
      <th style="padding:8px;text-align:center;">Qty</th>
      <th style="padding:8px;text-align:right;">Unit</th>
      <th style="padding:8px;text-align:right;">Total</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <p style="text-align:right;margin:4px 0;">Subtotal: ${formatMoney(order.subtotal)}</p>
  <p style="text-align:right;margin:4px 0;">Shipping: ${formatMoney(order.shipping)}</p>
  <p style="text-align:right;margin:4px 0;">Tax: ${formatMoney(order.tax)}</p>
  <p style="text-align:right;font-size:18px;font-weight:bold;margin:12px 0;">Total: ${formatMoney(order.total)} CAD</p>
  ${paymentBlock}
  <p style="margin-top:24px;font-size:12px;color:#666;">Questions? Reply to this email or contact ${ORDERS_EMAIL_ADDRESS}</p>
</body></html>`;

  if (isMailOrderTest(order)) {
    html = decorateTestInvoiceHtml(html);
  }

  return html;
}

export async function sendMailOrderInvoiceEmail(order, { checkInstructions = '' } = {}) {
  if (!order?.customer_email) {
    throw new Error('Customer email is required to send an invoice.');
  }

  const subjectPrefix = isMailOrderTest(order) ? '[TEST] ' : '';
  const response = await fetch('/api/v1/email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to: order.customer_email,
      from: ORDERS_EMAIL_FROM,
      replyTo: ORDERS_EMAIL_ADDRESS,
      subject: `${subjectPrefix}Invoice for Order ${order.order_number}`,
      body: buildSimpleInvoiceHtml(order, checkInstructions),
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to send invoice for ${order.order_number}`);
  }
}
